import { useEffect, useMemo } from 'react';
import { Select, ShareWithField, TextArea, Toggle } from '../../../../components';
import { COOLDOWN_PERIOD_OPTIONS, TIME_LIMIT_OPTIONS } from '../../../../constants';
import {
  useAppDispatch,
  useAppSelector,
  useFeatureFlag,
  useGetCoachingTemplateOptions,
  useGetScorecardTemplateOptions,
} from '../../../../hooks';
import {
  setAllowedCallers,
  setAllowedTeams,
  setCoachingTemplateId,
  setCooldownPeriod,
  setIsSubscribed,
  setManagerNotes,
  setScorecardTemplateId,
  setTimeLimit,
} from '../../../../redux/reducers';
import { useGetOrganizationSettingsQuery } from '../../../../services';
import { LD_FeatureFlags, Permissions, ProspectCategory, ProspectPageMode } from '../../../../types';
import { conditionalArray } from '../../../../utils';
import ProspectFieldGroup from '../ProspectFieldGroup';
import ProspectPageField from '../ProspectPageField';
import TabSection from '../TabSection';
import FolderLocationField from './FolderLocationField';

const MIN_MANAGER_NOTES_ROWS = 1;
const MAX_MANAGER_NOTES_ROWS = 5;

const SimSettings = () => {
  const dispatch = useAppDispatch();

  const coachingRubricFF = useFeatureFlag(LD_FeatureFlags.RELEASE_COACHING_RUBRICS);
  const {
    category,
    orgId,
    mode,
    fields: {
      scorecardTemplateId,
      coachingTemplateId,
      cooldownPeriod,
      timeLimit,
      managerNotes,
      folderId,
      allowedCallers,
      allowedTeams,
      isSubscribed,
    },
  } = useAppSelector((state) => state.prospect);

  const { options: scorecardTemplateOptions, isLoading: isLoadingScorecardTemplates } = useGetScorecardTemplateOptions({
    orgId,
    selectedTemplateId: scorecardTemplateId,
  });

  const { options: coachingTemplateOptions, isLoading: isLoadingCoachingTemplates } = useGetCoachingTemplateOptions({
    orgId,
    selectedTemplateId: coachingTemplateId,
  });

  const { data: orgConfigs, isLoading: isLoadingOrgSettings } = useGetOrganizationSettingsQuery({
    orgId,
  });
  const { defaultScorecardTemplateId, defaultCoachingTemplateId, repCanEditLibrary = false } = orgConfigs || {};

  const user = useAppSelector((state) => state.auth.user);
  const canManageProspect = (user?.permissions || []).includes(Permissions.MANAGE_PROSPECT);

  const isQuiz = category === ProspectCategory.QUIZ;
  const isInterview = category === ProspectCategory.INTERVIEW;

  const isShareable = !isInterview;

  // Set defaults when creating a new prospect
  // This only runs for super admins, because they are the only ones
  // who can see the default scorecard and coaching template fields on creation.
  useEffect(() => {
    if (isLoadingOrgSettings || mode !== ProspectPageMode.CREATE) return;

    // Set default scorecard template ID
    if (!scorecardTemplateId && defaultScorecardTemplateId) {
      dispatch(setScorecardTemplateId(defaultScorecardTemplateId));
    }
    // Set default coaching template ID
    if (!coachingTemplateId && defaultCoachingTemplateId) {
      dispatch(setCoachingTemplateId(defaultCoachingTemplateId));
    }

    // We intentionally don't include scorecardTemplateId and coachingTemplateId as dependencies
    // because we don't want to reset to the default if it's cleared by the user
  }, [isLoadingOrgSettings, defaultScorecardTemplateId, dispatch, defaultCoachingTemplateId, mode]);

  const managerAiFieldGroup = useMemo(
    () => [
      {
        label: 'AI scorecard',
        content: (
          <Select
            clearable
            loading={isLoadingScorecardTemplates}
            options={scorecardTemplateOptions}
            selected={scorecardTemplateId}
            onChange={(newValue) => dispatch(setScorecardTemplateId(newValue))}
          />
        ),
      },
      ...conditionalArray(coachingRubricFF, {
        label: 'AI coaching',
        content: (
          <Select
            clearable
            loading={isLoadingCoachingTemplates}
            options={coachingTemplateOptions}
            selected={coachingTemplateId}
            onChange={(newValue) => dispatch(setCoachingTemplateId(newValue))}
          />
        ),
      }),
    ],
    [
      isLoadingScorecardTemplates,
      scorecardTemplateId,
      scorecardTemplateOptions,
      dispatch,
      coachingRubricFF,
      coachingTemplateId,
      isLoadingCoachingTemplates,
      coachingTemplateOptions,
    ]
  );

  const managerQuizFieldGroup = useMemo(
    () => [
      {
        label: 'Cool-down period',
        tooltip: 'Require wait before next attempt.',
        content: (
          <Select
            clearable
            options={COOLDOWN_PERIOD_OPTIONS}
            selected={cooldownPeriod?.toString()}
            onChange={(newValue) => dispatch(setCooldownPeriod(newValue ? parseInt(newValue) : undefined))}
          />
        ),
      },
      {
        label: 'Time limit',
        tooltip: 'Limit the length of the quiz.',
        content: (
          <Select
            clearable
            options={TIME_LIMIT_OPTIONS}
            selected={timeLimit?.toString()}
            onChange={(newValue) => dispatch(setTimeLimit(newValue ? parseInt(newValue) : undefined))}
          />
        ),
      },
    ],
    [cooldownPeriod, timeLimit, dispatch]
  );

  return (
    <TabSection title="Settings">
      {canManageProspect && (
        <>
          <ProspectFieldGroup fields={managerAiFieldGroup} />
          {isQuiz && <ProspectFieldGroup fields={managerQuizFieldGroup} />}
          <ProspectPageField
            label="Manager notes"
            tooltip="Only visible to managers and administrators."
            content={
              <TextArea
                fitContent
                maxRows={MAX_MANAGER_NOTES_ROWS}
                rows={MIN_MANAGER_NOTES_ROWS}
                value={managerNotes}
                onChange={(e) => dispatch(setManagerNotes(e.target.value))}
              />
            }
          />
        </>
      )}
      {(canManageProspect || repCanEditLibrary) && (
        <ProspectPageField
          label="Folder location"
          required
          content={<FolderLocationField folderId={folderId} orgId={orgId} />}
        />
      )}
      {isShareable && canManageProspect && (
        <>
          <ProspectPageField
            label="Share with"
            content={
              <ShareWithField
                selectedCallers={allowedCallers}
                selectedTeams={allowedTeams}
                setSelectedCallers={(newCallers) => dispatch(setAllowedCallers(newCallers))}
                setSelectedTeams={(newTeams) => dispatch(setAllowedTeams(newTeams))}
              />
            }
          />
          <ProspectPageField
            label="Notify me upon completion"
            content={
              <Toggle
                checked={isSubscribed}
                rightLabel={isSubscribed ? 'Yes' : 'No'}
                onChange={(newValue) => dispatch(setIsSubscribed(newValue))}
              />
            }
          />
        </>
      )}
    </TabSection>
  );
};

export default SimSettings;
